import { apiClient } from './client';

export interface TeamMember {
  id: number;
  team_id: number;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  joined_at: string;
  profiles?: { email: string };
}

export interface Team {
  id: number;
  name: string;
  billing_email: string;
  credits: number;
  /**
   * Sum of pending credit holds for in-flight generations.
   * Optional — present on `GET /team` responses post credit-hold-unification.
   * Older clients (or migration rollback) may omit it; consumers should default to 0.
   */
  held_credits?: number;
  /**
   * Spendable credits = `credits` − `held_credits`.
   * Optional — present on `GET /team` responses post credit-hold-unification.
   * Older clients (or migration rollback) may omit it; consumers should fall back to `credits`.
   */
  available_credits?: number;
  /**
   * Set when the team has purchased its one-time testing package — the
   * webhook stamps it on the first settled trial payment. Gates the trial
   * offer in the billing UI (server enforces the same rule).
   */
  trial_used_at?: string | null;
  inference_discount_pct?: number | null;
  purchase_discount_pct?: number | null;
  /** PR-4: per-team usage.balance_low webhook threshold; null = disabled. */
  balance_low_threshold?: number | null;
  // Company vetting profile — collected on /pending while awaiting approval.
  // All optional/nullable; the API persists empty input as NULL.
  company_name?: string | null;
  company_address?: string | null;
  company_postcode?: string | null;
  /** ISO 3166-1 alpha-2 code (uppercase). */
  company_country?: string | null;
  /** VAT / EIN / UEI (free text). */
  company_tax_id?: string | null;
  /** Telegram handle, normalized (no @, lowercase). */
  contact_telegram?: string | null;
  /** Company/billing contact email; the /pending UI defaults it to the signup email. */
  company_email?: string | null;
  team_members: TeamMember[];
}

export interface TeamInvite {
  id: string;
  team_id: number;
  email: string;
  role: 'admin' | 'member';
  invited_by: string;
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  token: string;
  created_at: string;
  expires_at: string;
}

export const teamService = {
  async getMyTeams(): Promise<Team[]> {
    const response = await apiClient.get<Team[]>('/team');
    return response.data;
  },

  async addMember(teamId: number, email: string, role: 'admin' | 'member'): Promise<void> {
    await apiClient.post(`/team/${teamId}/members`, { email, role });
  },

  async removeMember(teamId: number, userId: string): Promise<void> {
    await apiClient.delete(`/team/${teamId}/members/${userId}`);
  },

  async createTeam(name: string): Promise<Team> {
    const response = await apiClient.post<Team>('/team', { name });
    return response.data;
  },

  async getInvites(teamId: number): Promise<TeamInvite[]> {
    const response = await apiClient.get<TeamInvite[]>(`/team/${teamId}/invites`);
    return response.data;
  },

  async createInvite(teamId: number, email: string, role: 'admin' | 'member'): Promise<TeamInvite> {
    const response = await apiClient.post<TeamInvite>(`/team/${teamId}/invites`, { email, role });
    return response.data;
  },

  async cancelInvite(teamId: number, inviteId: string): Promise<void> {
    await apiClient.delete(`/team/${teamId}/invites/${inviteId}`);
  },

  async acceptInvite(token: string): Promise<unknown> {
    const response = await apiClient.post('/invite/accept', { token });
    return response.data;
  },

  async transferOwnership(teamId: number, newOwnerId: string): Promise<void> {
    await apiClient.post(`/team/${teamId}/transfer-ownership`, { newOwnerId });
  },

  async updateMemberRole(teamId: number, userId: string, role: 'admin' | 'member'): Promise<void> {
    await apiClient.patch(`/team/${teamId}/members/${userId}/role`, { role });
  },

  /**
   * Rename (or otherwise update) a team. Owner/admin only on the server.
   * The onboarding wizard's intent-picker uses this to persist the
   * workspace name the user types before they advance to step 2.
   */
  async updateTeam(
    teamId: number,
    dto: {
      name?: string;
      company_name?: string;
      company_address?: string;
      company_postcode?: string;
      company_country?: string;
      company_tax_id?: string;
      contact_telegram?: string;
      company_email?: string;
    },
  ): Promise<Team> {
    const response = await apiClient.patch<Team>(`/team/${teamId}`, dto);
    return response.data;
  },
};
